/**
 * Razorpay utility — shared payment integration
 *
 * Handles:
 * - Loading Razorpay checkout script
 * - Opening Razorpay modal (supports both orders and subscriptions)
 *
 * Usage:
 *   import { loadRazorpayScript, openRazorpay } from "../../utils/razorpay";
 *
 *   await loadRazorpayScript();
 *   openRazorpay({
 *     amount, order_id|subscription_id, description,
 *     onSuccess: async (response) => { ... },
 *     onDismiss: () => { ... },
 *   });
 */

let scriptLoadPromise = null;

function loadScript() {
  if (window.Razorpay) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;
  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => { scriptLoadPromise = null; resolve(); };
    script.onerror = () => { scriptLoadPromise = null; reject(new Error("Failed to load Razorpay SDK")); };
    document.body.appendChild(script);
  });
  return scriptLoadPromise;
}

export async function loadRazorpayScript() {
  return loadScript();
}

export function openRazorpay({
  amount,
  order_id,
  subscription_id,
  currency = "INR",
  name = "PepperDiamond",
  description,
  prefill = {},
  themeColor = "#ff9900",
  onSuccess,
  onDismiss,
}) {
  const key = process.env.REACT_APP_RAZORPAY_KEY_ID;

  if (!window.Razorpay) {
    console.error("Razorpay SDK not loaded");
    return;
  }

  const options = {
    key,
    amount,
    currency,
    name,
    description,
    prefill,
    theme: { color: themeColor },
    handler: async function (response) {
      try {
        await onSuccess?.(response);
      } catch (err) {
        console.error("Razorpay success handler error:", err);
      }
    },
    modal: {
      ondismiss: function () {
        onDismiss?.();
      },
    },
  };

  // Support both order_id and subscription_id
  if (subscription_id) {
    options.subscription_id = subscription_id;
  } else if (order_id) {
    options.order_id = order_id;
  }

  const rzp = new window.Razorpay(options);
  rzp.open();
}